import { useState } from 'react';
import { C, DEGRADE_ACTION, OMBRE_ACTION } from '../../theme';
import { SousTitreSection, Squelette, Panne } from '../composants/base';
import { api } from '../../api/routes';
import { useDonnees, messageDErreur } from '../../api/useDonnees';
import { useCocher } from '../cocher';
import { AjouterElement } from '../composants/AjouterElement';
import { LigneElement } from '../composants/LigneElement';
import { EnteteListe } from './Planning';
import { Panier, Etoile } from '../../icones';
import { BoutonDeuxGestes } from '../composants/BoutonDeuxGestes';
import { aujourdhuiDans, jourEnToutesLettres } from '../format';
import type { CaseRepas, JourRepas, ListeCochable } from '../../api/types';

/**
 * La semaine de cuisine (maquette validee le 2026-08-26) : deux creneaux par jour,
 * dejeuner d'abord — c'est le grand repas a Dakar. Remplir une case est une ecriture
 * directe, sans IA ; seule la liste de courses appelle le modele (un appel, facture),
 * et elle passe par le circuit normal des propositions : rien sans validation.
 */
export function Repas({
  fuseau,
  version,
  signaler,
  rafraichir,
  ouvrirCompris,
}: {
  fuseau: string;
  version: number;
  signaler: (message: string) => void;
  rafraichir: () => void;
  ouvrirCompris: (elementId: string) => void;
}) {
  const etat = useDonnees((signal) => api.repas(signal), version);
  const [occupe, setOccupe] = useState(false);
  const { basculer } = useCocher(signaler, rafraichir);

  if (etat.chargement && etat.donnees === null)
    return <Squelette lignes={4} texte="Chargement des repas…" />;
  if (etat.erreur !== null && etat.donnees === null) {
    return <Panne message={etat.erreur} onReessayer={etat.recharger} />;
  }

  const jours = etat.donnees?.jours ?? [];
  const lundi = etat.donnees?.lundi ?? '';
  const aujourdhui = aujourdhuiDans(fuseau);
  const semaineCourante = jours.slice(0, 7);
  const semaineSuivante = jours.slice(7);
  const courantePleine = semaineCourante.some((j) => j.dejeuner !== null || j.diner !== null);

  const preparerCourses = async () => {
    if (occupe) return;
    setOccupe(true);
    try {
      const { item } = await api.listeCoursesDeLaSemaine(lundi);
      // La liste attend dans la boite : l'ecran de validation prend la main.
      ouvrirCompris(item.id);
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <>
      <SousTitreSection>Cette semaine</SousTitreSection>
      {semaineCourante.map((jour) => (
        <JourDeCuisine
          key={jour.date}
          jour={jour}
          estAujourdhui={jour.date === aujourdhui}
          signaler={signaler}
          rafraichir={rafraichir}
        />
      ))}

      {/* Le texte d'aide entre DANS le bouton quand il est desactive : une consigne
          orpheline sous un bouton gris n'explique rien (retour du porteur). */}
      <button
        type="button"
        className="pressable"
        disabled={occupe || !courantePleine}
        onClick={() => void preparerCourses()}
        style={{
          minHeight: 50,
          borderRadius: 14,
          padding: '8px 16px',
          background: occupe || !courantePleine ? C.carteDouce : DEGRADE_ACTION,
          color: occupe || !courantePleine ? C.tertiaire : '#FFFFFF',
          border: occupe || !courantePleine ? `1px solid ${C.bordure}` : 'none',
          fontSize: 15,
          fontWeight: 800,
          fontFamily: 'inherit',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          cursor: occupe || !courantePleine ? 'default' : 'pointer',
          boxShadow: occupe || !courantePleine ? 'none' : OMBRE_ACTION,
        }}
      >
        <Etoile
          taille={18}
          couleur={occupe || !courantePleine ? C.tertiaire : '#FFFFFF'}
          epaisseur={1.8}
        />
        <Panier
          taille={20}
          couleur={occupe || !courantePleine ? C.tertiaire : '#FFFFFF'}
          epaisseur={1.9}
        />
        <span style={{ textAlign: 'left', lineHeight: 1.35 }}>
          {occupe
            ? 'Colette réfléchit…'
            : courantePleine
              ? 'Préparer la liste de courses'
              : 'Pose d’abord au moins un repas cette semaine'}
        </span>
      </button>
      {courantePleine && !occupe ? (
        <div style={{ fontSize: 12, color: C.tertiaire, textAlign: 'center', marginTop: -6 }}>
          Seul ce bouton appelle Colette. La liste passe ensuite par ta validation.
        </div>
      ) : null}

      {(etat.donnees?.listes ?? []).map((liste) => (
        <CarteListeDeCourses
          key={liste.id}
          liste={liste}
          basculer={basculer}
          signaler={signaler}
          rafraichir={rafraichir}
        />
      ))}

      <SousTitreSection>Semaine prochaine</SousTitreSection>
      {semaineSuivante.map((jour) => (
        <JourDeCuisine
          key={jour.date}
          jour={jour}
          estAujourdhui={false}
          signaler={signaler}
          rafraichir={rafraichir}
        />
      ))}
    </>
  );
}

/** Une liste de courses validee : cochable au magasin, comme dans le planning. */
function CarteListeDeCourses({
  liste,
  basculer,
  signaler,
  rafraichir,
}: {
  liste: ListeCochable;
  basculer: (type: 'tache' | 'element', id: string, fait: boolean) => void;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <EnteteListe
        liste={liste}
        signaler={signaler}
        rafraichir={rafraichir}
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 0.8,
          color: C.mentheTexte,
          flex: 1,
        }}
      />
      {liste.elements.map((element) => (
        <LigneElement
          key={element.id}
          element={element}
          basculer={basculer}
          signaler={signaler}
          rafraichir={rafraichir}
        />
      ))}
      <AjouterElement
        checklistId={liste.id}
        titreListe={liste.titre}
        signaler={signaler}
        rafraichir={rafraichir}
      />
    </div>
  );
}

function JourDeCuisine({
  jour,
  estAujourdhui,
  signaler,
  rafraichir,
}: {
  jour: JourRepas;
  estAujourdhui: boolean;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.encre }}>
        {jourEnToutesLettres(jour.date)}
        {estAujourdhui ? ' — aujourd’hui' : ''}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <CaseDeRepas
          date={jour.date}
          creneau="DEJEUNER"
          repas={jour.dejeuner}
          signaler={signaler}
          rafraichir={rafraichir}
        />
        <CaseDeRepas
          date={jour.date}
          creneau="DINER"
          repas={jour.diner}
          signaler={signaler}
          rafraichir={rafraichir}
        />
      </div>
    </div>
  );
}

/** Une case de repas : plat affiche, ou saisie directe — zero IA pour taper un plat. */
function CaseDeRepas({
  date,
  creneau,
  repas,
  signaler,
  rafraichir,
}: {
  date: string;
  creneau: 'DEJEUNER' | 'DINER';
  repas: CaseRepas | null;
  signaler: (message: string) => void;
  rafraichir: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const libelle = creneau === 'DEJEUNER' ? 'Déjeuner' : 'Dîner';

  const poser = async () => {
    const titre = saisie.trim();
    if (titre === '' || envoi) return;
    setEnvoi(true);
    try {
      await api.poserRepas({ date, creneau, titre });
      setEdition(false);
      setSaisie('');
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  const vider = async () => {
    if (envoi) return;
    setEnvoi(true);
    try {
      await api.viderRepas({ date, creneau });
      setEdition(false);
      setSaisie('');
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  if (edition) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void poser();
        }}
        style={{
          flex: 1,
          minWidth: 0,
          background: C.carte,
          border: `1px solid ${C.violetBordure}`,
          borderRadius: 14,
          padding: '8px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <input
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Quel plat ?"
          aria-label={`${libelle} du ${jourEnToutesLettres(date)}`}
          autoFocus
          style={{
            width: '100%',
            height: 38,
            border: `1px solid ${C.bordure}`,
            borderRadius: 10,
            padding: '0 10px',
            fontSize: 16,
            fontFamily: 'inherit',
            color: C.encre,
            // Sans fond declare, un input prend le BLANC du navigateur — et l'encre de
            // Colette etant claire, on n'y voit plus ce qu'on tape.
            background: C.carte,
            outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="submit"
            disabled={envoi || saisie.trim() === ''}
            style={{
              flex: 1,
              height: 34,
              border: 'none',
              borderRadius: 9,
              background: envoi || saisie.trim() === '' ? C.inactif : DEGRADE_ACTION,
              color: '#FFFFFF',
              fontSize: 13,
              fontWeight: 800,
              fontFamily: 'inherit',
              cursor: envoi || saisie.trim() === '' ? 'default' : 'pointer',
            }}
          >
            OK
          </button>
          {/* « Vider » n'est propose que s'il y a un plat — mais « Annuler » est TOUJOURS
              la : ouvrir une case par erreur ne doit pas obliger a re-valider ou a
              effacer (piege releve au lot D). */}
          {repas !== null ? (
            <BoutonDeuxGestes
              libelle="Vider"
              libelleArme="Confirmer ?"
              aria="Vider"
              ariaArme={`Confirmer : vider le ${libelle.toLowerCase()} du ${jourEnToutesLettres(date)}`}
              onConfirmer={() => void vider()}
              occupe={envoi}
              style={{
                height: 34,
                padding: '0 10px',
                border: `1px solid ${C.bordure}`,
                borderRadius: 9,
                background: C.carte,
                color: C.corailTexte,
                fontSize: 13,
                fontWeight: 800,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
              styleArme={{ borderColor: C.corailTexte }}
            />
          ) : null}
          <button
            type="button"
            className="pressable"
            onClick={() => {
              setSaisie(repas?.titre ?? '');
              setEdition(false);
            }}
            style={{
              height: 34,
              padding: '0 10px',
              border: `1px solid ${C.bordure}`,
              borderRadius: 9,
              background: C.carte,
              color: C.secondaire,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            Annuler
          </button>
        </div>
      </form>
    );
  }

  return (
    <button
      type="button"
      className="pressable"
      aria-label={`${libelle} du ${jourEnToutesLettres(date)}${repas === null ? '' : ` : ${repas.titre}`}`}
      onClick={() => {
        setSaisie(repas?.titre ?? '');
        setEdition(true);
      }}
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 64,
        background: C.carte,
        border: repas === null ? `1px dashed ${C.violetBordure}` : `1px solid ${C.bordure}`,
        borderRadius: 14,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: repas === null ? 'center' : 'flex-start',
        justifyContent: 'center',
        gap: 3,
        textAlign: 'left',
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {repas === null ? (
        <span style={{ color: C.violetTexte, fontSize: 13.5, fontWeight: 800 }}>+ Ajouter</span>
      ) : (
        <>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: 0.8,
              color: C.tertiaire,
            }}
          >
            {libelle.toUpperCase()}
          </span>
          <span style={{ fontSize: 14.5, fontWeight: 700, color: C.encre }}>{repas.titre}</span>
        </>
      )}
    </button>
  );
}
